import { AfterViewInit, Component, Input, OnInit, ElementRef, ViewChild, HostListener } from '@angular/core';
import { WorkflowService } from 'src/app/services';
import { ToastrService } from 'ngx-toastr';
import { Registration } from 'src/app/models/registration';
import { RegistrationDocument } from 'src/app/models/registration-document';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';
import { Utilities } from 'src/app/services/utilities';

@Component({
  selector: 'wm-renewals-janus-view-details',
  standalone: false,
  templateUrl: './renewals-janus-view-details.component.html',
  styleUrls: ['./renewals-janus-view-details.component.scss']
})
export class RenewalsJanusViewDetailsComponent implements OnInit, AfterViewInit {

  private _row: any;
  private lastFetchedId: string | null = null;
  private pendingComplianceDocDeleteId: string | null = null;

  activeTab: 'docs' | 'fields' | 'notes' = 'docs';
  @ViewChild('tabsContainer')
  tabsContainer!: ElementRef<HTMLElement>;

  showTabArrows = true;
  @Input()
  set row(value: any) {
    this._row = value;
    // try fetch as soon as row arrives
    this.tryFetch();
  }
  get row() {
    return this._row;
  }

  @Input() closeExpansion!: () => void;

  // activeTab: 'docs' | 'fields' | 'notes' = 'docs';

  // Start code added by Sanchit on 25-03-2026
  tabs = [
    { id: 'docs', label: 'Compliance Docs' },
    { id: 'fields', label: 'Additional Fields' },
    { id: 'notes', label: 'Notes' }
  ];
  // End code added by Sanchit on 25-03-2026

  registration!: Registration;
  isLoading = false;

  constructor(
    private _workflowSvc: WorkflowService,
    private _toastrSvc: ToastrService
  ) { }

  // Added by Amisha on 2026-06-29.
  // Reason: Staff-side "apply on behalf" renewals should show applicant contact data
  // from registration custom fields instead of the staff user who created the record.
  get applicantName(): string {
    return (
      this.getCustomFieldDisplayValue(['AppName', 'ApplicantName']) ||
      this.registration?.createdBy ||
      ''
    );
  }

  // Added by Amisha on 2026-06-29.
  // Reason: Staff-side "apply on behalf" renewals should show applicant contact data
  // from registration custom fields instead of the staff user who created the record.
  get applicantEmail(): string {
    return (
      this.getCustomFieldDisplayValue(['AppEmail', 'ApplicantEmail']) ||
      this.registration?.createdBy ||
      ''
    );
  }

  // Added by Amisha on 2026-06-29.
  // Reason: Staff-side "apply on behalf" renewals should show applicant contact data
  // from registration custom fields instead of the staff user who created the record.
  get applicantPhone(): string {
    return (
      this.getCustomFieldDisplayValue(['AppPhone', 'ApplicantPhone']) ||
      (this.registration as any)?.phoneNo ||
      (this.registration as any)?.phoneNumber ||
      (this.registration as any)?.phone ||
      ''
    );
  }

  // Added by Copilot on 2026-08-06.
  // Reason: Amount Paid must reflect the sum of confirmed PaymentRequest amounts on
  // the registration's application (surfaced by the server as registration.amountPaid).
  // FeePayments only link fees to payment requests and do not carry a paid amount.
  get amountPaidDisplay(): string {
    const amount = Number(this.registration?.amountPaid ?? 0);
    const safeAmount = Number.isFinite(amount) ? amount : 0;
    return Utilities.formatCurrency(safeAmount);
  }

  updateOverflowTitle(event: Event, text: string | null | undefined): void {
    const host = event.currentTarget as HTMLElement | null;
    if (!host) {
      return;
    }

    const measuredElement =
      (host.querySelector('.common-text') as HTMLElement | null) ?? host;
    const normalizedText = (text ?? '').trim();
    const shouldShow =
      !!normalizedText &&
      measuredElement.scrollWidth > measuredElement.clientWidth;

    if (shouldShow) {
      host.setAttribute('title', normalizedText);
      host.classList.add('is-truncated');
    } else {
      host.removeAttribute('title');
      host.classList.remove('is-truncated');
    }
  }

  ngOnInit(): void {
    // ✅ confirms component is created
    this.tryFetch();
  }
    ngAfterViewInit(): void {
      setTimeout(() => {
        this.checkTabsOverflow();
      });
    }

    @HostListener('window:resize')
    onWindowResize(): void {
      this.checkTabsOverflow();
    }
  confirmationModalData: CommonConfirmationModalData = {
    open: false,
    modalDescp: '',
    modalSubDescp: '',
    okBtn: true,
    replaceOkWith: 'OK',
    status: 'default'
  };

  private openInfoConfirmation(
    desc: string,
    subDesc: string,
    status: 'success' | 'error' | 'info' | 'default' = 'default'
  ) {
    this.confirmationModalData = {
      open: true,
      modalDescp: desc,
      modalSubDescp: subDesc,
      status,
      yesNoBtn: false,
      okBtn: true,
      closeBtn: false,
      replaceOkWith: 'OK',
      customCloseAccepted: true
    };
  }

  private openDeleteConfirmation(documentId: string): void {
    this.pendingComplianceDocDeleteId = documentId;
    this.confirmationModalData = {
      open: true,
      modalDescp: 'Are you sure you want to delete this document?',
      modalSubDescp: 'This action cannot be undone.',
      yesNoBtn: true,
      okBtn: false,
      closeBtn: false,
      status: 'default',
      customCloseAccepted: true,
      replaceYesWith: 'YES',
      replaceNoWith: 'NO'
    };
  }

  closeConfirmation(): void {
    this.confirmationModalData = {
      open: false
    };
    this.pendingComplianceDocDeleteId = null;
  }

  onConfirmationYes(): void {
    const documentId = this.pendingComplianceDocDeleteId;

    this.closeConfirmation();

    if (!documentId) {
      return;
    }

    this.onComplianceDocDeleted(documentId);
  }

  onComplianceDocDeleteRequested(id: string): void {
    if (!id) return;

    this.openDeleteConfirmation(id);
  }

  private getRegistrationIdFromRow(r: any): string | null {
    return (
      r?.registrationInfo?.id ||
      r?.registrationInfo?.registrationId ||
      r?.registrationId ||
      r?.id ||
      null
    );
  }

  // Added by Amisha on 2026-06-16.
  // Reason: Renewals Janus view details should show the same icon-first status treatment
  // used in applicant mobile views without affecting non-Janus summary cards.
  getStatusMeta(status: number): { text: string; icon: string; color: string } {
    const text = this.getStatusText(status);

    switch (text) {
      case 'Approved':
      case 'Renewed':
        return {
          text,
          icon: 'check_circle',
          color: '#2E7D32'
        };
      case 'Pending':
      case 'Denied':
      case 'Expired':
        return {
          text,
          icon: 'error',
          color: '#D32F2F'
        };
      case 'Inactive':
      case 'Deleted':
      case 'Disabled':
        return {
          text,
          icon: 'remove_circle',
          color: '#9E9E9E'
        };
      default:
        return {
          text,
          icon: 'help',
          color: '#9E9E9E'
        };
    }
  }

  getStatusText(status: number): string {
    const map: any = {
      0: 'Pending',
      1: 'Approved',
      2: 'Denied',
      3: 'Expired',
      4: 'Inactive',
      5: 'Deleted',
      6: 'Renewed',
      7: 'Disabled',
    };

    return map[status] || 'Unknown';
  }

  // Added by Amisha on 2026-06-29.
  // Reason: Staff-side "apply on behalf" renewals store applicant contact values in
  // registration custom fields, so Janus details must prefer template-code lookups
  // over registration.createdBy to show the actual applicant.
  private getCustomFieldDisplayValue(templateCodes: string[]): string {
    const customField = (this.registration?.customFields || []).find(field =>
      templateCodes.includes(field?.templateCode)
    );

    if (!customField) {
      return '';
    }

    return this.extractFieldValue(customField.fieldValue, customField.dataEntityType);
  }

  // Added by Amisha on 2026-06-29.
  // Reason: Renewal custom field values may be plain text, list-data JSON, or
  // calculated-value JSON, so the summary card needs one parser that normalizes them
  // into displayable strings.
  private extractFieldValue(fieldValue: any, dataEntityType?: string): string {
    if (fieldValue == null || fieldValue === '') {
      return '';
    }

    if (typeof fieldValue !== 'string') {
      return String(fieldValue);
    }

    const trimmedValue = fieldValue.trim();

    if (!trimmedValue) {
      return '';
    }

    if (dataEntityType === 'list-data') {
      try {
        const parsedList = JSON.parse(trimmedValue);
        return parsedList?.[0]?.text || parsedList?.[0]?.value || '';
      } catch {
        return '';
      }
    }

    if (trimmedValue.startsWith('{') || trimmedValue.startsWith('[')) {
      try {
        const parsedValue = JSON.parse(trimmedValue);

        if (Array.isArray(parsedValue)) {
          return parsedValue?.[0]?.text || parsedValue?.[0]?.value || '';
        }

        if (typeof parsedValue === 'object' && parsedValue !== null) {
          return (
            parsedValue.EditedValue ||
            parsedValue.CalculatedValue ||
            parsedValue.Value ||
            ''
          );
        }
      } catch {
        return trimmedValue;
      }
    }

    return trimmedValue;
  }


  private tryFetch(): void {
    const id = this.getRegistrationIdFromRow(this._row);
    if (!id) return;

    // avoid refetch spam
    if (this.lastFetchedId === id) return;
    this.lastFetchedId = id;

    this.fetchRegistration(id);
  }

  private fetchRegistration(registrationId: string): void {
    this.isLoading = true;

    this._workflowSvc.getRegistration(registrationId).subscribe({
      next: (data) => {
        this.registration = data;

        this.isLoading = false;
      },
      error: () => {
        this._toastrSvc.error('Failed to load registration details');
        this.isLoading = false;
      }
    });
  }

 setTab(tab: 'docs' | 'fields' | 'notes') {
  this.activeTab = tab;
}

// Start code added by Sanchit on 25-03-2026
getTabButtonData(tab: any) {
  return {
    text: tab.label,
    onClick: () => this.setTab(tab.id),
    class: 'janus-category-list-button'
  };
}
// End code added by Sanchit on 25-03-2026

private checkTabsOverflow(): void {
  const container = this.tabsContainer?.nativeElement;

  if (!container) {
    return;
  }

  this.showTabArrows =
    container.scrollWidth > container.clientWidth + 1;
}

scrollTabs(direction: 'left' | 'right'): void {
  const container = this.tabsContainer?.nativeElement;

  if (!container) {
    return;
  }

  const scrollAmount = 150;

  container.scrollBy({
    left: direction === 'right'
      ? scrollAmount
      : -scrollAmount,
    behavior: 'smooth'
  });
}

updateTabArrows(): void {
  this.checkTabsOverflow();
}
  /* ================= COMPLIANCE DOCS API ================= */

  onComplianceDocsAdded(documents: RegistrationDocument[]) {

    if (!documents || documents.length === 0) return;

    const registrationId = this.registration?.id;
    if (!registrationId) return;

    this.isLoading = true; // code added by Sanchit on 31-03-2026

    this._workflowSvc
      .saveRegistrationDocuments(registrationId, documents)
      .subscribe({
        next: () => {
          this.openInfoConfirmation(
            'Renewals',
            'Document saved successfully.',
            'success'
          );
          this.fetchRegistration(registrationId);
        },
        error: () => {
          this.isLoading = false; // code added by Sanchit on 31-03-2026
          this.openInfoConfirmation(
            'Renewals',
            'Failed to save document.',
            'error'
          );
        }
      });
  }


  onComplianceDocDeleted(id: string) {

    if (!id) return;

    this.isLoading = true; // code added by Sanchit on 31-03-2026

    this._workflowSvc
      .deleteRegistrationDocument(id)
      .subscribe({
        next: () => {
          this.openInfoConfirmation(
            'Renewals',
            'Document deleted successfully.',
            'success'
          );
          this.fetchRegistration(this.registration.id);
        },
        error: () => {
          this.isLoading = false; // code added by Sanchit on 31-03-2026
          this.openInfoConfirmation(
            'Renewals',
            'Failed to delete document.',
            'error'
          );
        }
      });
  }


  onComplianceDocSaved(doc: RegistrationDocument) {
    this.isLoading = true; // code added by Sanchit on 31-03-2026
    this._workflowSvc
      .saveRegistrationDocument(doc)
      .subscribe({
        next: () => {
          this.openInfoConfirmation(
            'Renewals',
            'Document updated successfully.',
            'success'
          );
          this.fetchRegistration(this.registration.id);
        },
        error: () => {
          this.isLoading = false; // code added by Sanchit on 31-03-2026
          this.openInfoConfirmation(
            'Renewals',
            'Failed to update document.',
            'error'
          );
        }
      });
  }


}
